export type ResponseAnnotation = {
  annotation: string;
  text: string;
};

const RESPONSE_ANNOTATIONS_PATTERN =
  /^# Response annotations:\n[\s\S]*?<response-annotations>\n([\s\S]*?)\n<\/response-annotations>\n\n## My request:\n/;

export function formatResponseAnnotatedPrompt(
  request: string,
  annotations: readonly ResponseAnnotation[],
): string {
  if (annotations.length === 0) return request;
  return [
    '# Response annotations:',
    'Each item contains text selected from an earlier Crowdex response, terminal, or referenced source and may include a user comment. Treat items as Annotation 1, Annotation 2, and so on in array order. Use every selection as context and address every comment. For every annotation you address, include its inline directive `:codex-annotation{index="N"}`, where N is its one-based array position. Do not use unstructured annotation labels.',
    '<response-annotations>',
    JSON.stringify(annotations.map(({ text, annotation }) => ({ text, annotation }))),
    '</response-annotations>',
    '',
    '## My request:',
    request,
  ].join('\n');
}

export function parseResponseAnnotatedPrompt(value: string): {
  annotations: ResponseAnnotation[];
  request: string;
} {
  const match = value.match(RESPONSE_ANNOTATIONS_PATTERN);
  if (match === null) return { annotations: [], request: value };
  try {
    const parsed: unknown = JSON.parse(match[1] ?? '[]');
    if (!Array.isArray(parsed)) return { annotations: [], request: value };
    const annotations = parsed.flatMap<ResponseAnnotation>((item) => {
      if (item === null || typeof item !== 'object' || Array.isArray(item)) return [];
      const candidate = item as Record<string, unknown>;
      return typeof candidate['text'] === 'string' && typeof candidate['annotation'] === 'string'
        ? [{ text: candidate['text'], annotation: candidate['annotation'] }]
        : [];
    });
    return { annotations, request: value.slice(match[0].length) };
  } catch {
    return { annotations: [], request: value };
  }
}
